import { useLocalSearchParams, useRouter } from "expo-router";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useForm } from "react-hook-form";
import { toast } from "sonner-native";
import { z } from "zod";

import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnCheckboxInput } from "@/components/VnCheckboxInput/VnCheckboxInput";
import { VnForm, VnFormField } from "@/components/VnForm";
import { VnScrollView } from "@/components/VnScrollView";
import { VnText } from "@/components/VnText/VnText";
import { VnTextInput } from "@/components/VnTextInput/VnTextInput";
import { useCreateNpsSurveyMutation } from "@/data/mutations/npsSurveysMutations";
import { useTranslator } from "@/hooks/useTranslate";
import { I18nKeyType } from "@/i18n";
import { cn } from "@/utils/tailwind";

const FIRST_ROW_SCORES = [0, 1, 2, 3, 4, 5];
const SECOND_ROW_SCORES = [6, 7, 8, 9, 10];
const ALL_SCORES = [...FIRST_ROW_SCORES, ...SECOND_ROW_SCORES];

const npsSurveyFormSchema = z.object({
  score: z.number().int().min(0).max(10).optional(),
  feedback: z.string().optional(),
  hasAllowedSharing: z.boolean(),
});

type NpsSurveyFormType = z.infer<typeof npsSurveyFormSchema>;

export const NpsSurveyScreen = () => {
  const { t } = useTranslator();
  const router = useRouter();

  const { applicationIds: applicationIdsParam } = useLocalSearchParams<{
    applicationIds?: string;
  }>();
  const applicationIds = applicationIdsParam?.split(",").filter(Boolean) ?? [];

  const { mutate: createNpsSurvey, isPending } = useCreateNpsSurveyMutation();

  const form = useForm<NpsSurveyFormType>({
    resolver: standardSchemaResolver(npsSurveyFormSchema),
    defaultValues: {
      score: undefined,
      feedback: "",
      hasAllowedSharing: false,
    },
  });

  const { control } = form;
  const score = form.watch("score");
  const hasScore = score !== undefined;
  // Passives (7-9) and promoters (10) get the testimonial opt-in
  const isPassiveOrPromoter = hasScore && score >= 7;

  const feedbackLabelI18nKey: I18nKeyType =
    score === 10
      ? "native:npsSurveyScreen.highFeedbackLabel"
      : isPassiveOrPromoter
        ? "native:npsSurveyScreen.mediumFeedbackLabel"
        : "native:npsSurveyScreen.lowFeedbackLabel";

  const feedbackPlaceholderI18nKey: I18nKeyType =
    score === 10
      ? "native:npsSurveyScreen.highFeedbackPlaceholder"
      : isPassiveOrPromoter
        ? "native:npsSurveyScreen.mediumFeedbackPlaceholder"
        : "native:npsSurveyScreen.lowFeedbackPlaceholder";

  const handleSubmit = (data: NpsSurveyFormType) => {
    if (data.score === undefined) {
      return;
    }

    const hasAllowedSharing = data.score >= 7 ? data.hasAllowedSharing : false;
    const trimmedFeedback = data.feedback?.trim();

    createNpsSurvey(
      {
        score: data.score,
        feedback: trimmedFeedback === "" ? undefined : trimmedFeedback,
        hasAllowedSharing,
        applicationIds,
      },
      {
        onSuccess: () => {
          toast.success(t("native:npsSurveyScreen.success"));
          router.replace("/(authed)");
        },
      },
    );
  };

  return (
    <VnBox className="bg-background h-full">
      <VnScrollView
        className="flex-1"
        contentContainerClassName="items-center px-6 pb-20 md:px-8">
        <VnBox className="w-full max-w-[386px] md:max-w-[760px]">
          <VnForm form={form} isInputOnlyForm className="gap-5 pb-6 pt-9">
            <VnBox className="gap-4">
              <VnText
                variant="headerOne"
                i18nKey="native:npsSurveyScreen.title"
              />
              <VnText
                variant="lg"
                className="text-muted-foreground"
                i18nKey="native:npsSurveyScreen.subtitle"
              />
            </VnBox>

            <VnFormField
              control={control}
              name="score"
              render={({ field }) => {
                const chip = (value: number) => {
                  const isSelected = field.value === value;

                  return (
                    <VnBox key={value} className="aspect-[25/28] flex-1">
                      <VnButton
                        variant="outline"
                        className={cn(
                          "h-full w-full rounded-xl p-0 sm:h-full",
                          isSelected
                            ? "bg-sidebar border-sidebar hover:bg-sidebar/90 active:bg-sidebar"
                            : "border-input bg-white",
                        )}
                        onPress={() => field.onChange(value)}>
                        <VnText
                          className={cn(
                            "font-primary-semibold text-lg leading-7",
                            isSelected
                              ? "text-secondary group-active:text-secondary web:group-hover:text-secondary"
                              : "text-foreground",
                          )}>
                          {String(value)}
                        </VnText>
                      </VnButton>
                    </VnBox>
                  );
                };

                return (
                  <>
                    {/* Compact (<md): two rows 6 + 5 */}
                    <VnBox className="gap-2 md:hidden">
                      <VnBox className="flex-row items-start gap-2">
                        {FIRST_ROW_SCORES.map(chip)}
                      </VnBox>
                      <VnBox className="flex-row items-start gap-2">
                        <VnBox className="flex-[0.5]" />
                        {SECOND_ROW_SCORES.map(chip)}
                        <VnBox className="flex-[0.5]" />
                      </VnBox>
                    </VnBox>

                    {/* Wide (md+): single row of all 11 */}
                    <VnBox className="hidden flex-row items-start gap-2 md:flex">
                      {ALL_SCORES.map(chip)}
                    </VnBox>
                  </>
                );
              }}
            />

            <VnBox className="gap-1">
              <VnBox className="flex-row items-center gap-1.5">
                <VnText variant="mdBold" className="text-muted-foreground">
                  0
                </VnText>
                <VnText
                  variant="md"
                  className="text-muted-foreground"
                  i18nKey="native:npsSurveyScreen.scaleLowLabel"
                />
              </VnBox>
              <VnBox className="flex-row items-center gap-1.5">
                <VnText variant="mdBold" className="text-muted-foreground">
                  10
                </VnText>
                <VnText
                  variant="md"
                  className="text-muted-foreground"
                  i18nKey="native:npsSurveyScreen.scaleHighLabel"
                />
              </VnBox>
            </VnBox>

            {hasScore && (
              <VnBox className="gap-5 pt-3">
                <VnTextInput
                  control={control}
                  name="feedback"
                  isMultiline
                  isRequired={false}
                  inputClassName="min-h-[92px] bg-white md:min-h-[104px]"
                  labelI18nKey={feedbackLabelI18nKey}
                  placeholderI18nKey={feedbackPlaceholderI18nKey}
                />
                {isPassiveOrPromoter && (
                  <VnCheckboxInput
                    control={control}
                    name="hasAllowedSharing"
                    isRequired={false}
                    inputClassName="bg-white"
                    checkboxItemLabelVariant="md"
                    shouldWrapOptionLabel
                    option={{
                      label: t("native:npsSurveyScreen.testimonialLabel"),
                    }}
                  />
                )}
              </VnBox>
            )}
          </VnForm>
        </VnBox>
      </VnScrollView>

      <VnBox className="border-border bg-background items-center border-t px-6 py-4 md:px-8">
        <VnButton
          className="h-12 w-full max-w-[386px] rounded-full md:max-w-[760px]"
          textClassName="font-primary-semibold text-base leading-5"
          i18nKey="native:npsSurveyScreen.submitButton"
          isLoading={isPending}
          isDisabled={!hasScore || applicationIds.length === 0}
          onPress={() => {
            void form.handleSubmit(handleSubmit)();
          }}
        />
      </VnBox>
    </VnBox>
  );
};
